'use client';


/** Imports */
import React from 'react';
import classNames from 'classnames';
import {useTranslations} from 'next-intl';
import styles from './ErrorBoundary.module.css';


/** Types */
type ErrorFallbackProps = Readonly<{
    error: Error;
    resetErrorBoundary: () => void;
}>;
export type ErrorBoundaryProps = {
    children: React.ReactNode;
    /**
     * Optional custom fallback render function.
     * If not provided, the default <ErrorFallback /> will be used.
     */
    fallbackRender?: (props: ErrorFallbackProps) => React.ReactNode;
    /**
     * Optional function that is called after the error has been captured.
     */
    onErrorCaptured?: (error: Error, errorInfo: React.ErrorInfo) => void;
};
type ErrorBoundaryState = {
    error: Error | null;
};


/** Component */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {error: null};
        this.resetErrorBoundary = this.resetErrorBoundary.bind(this);
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {

        // Update state so the next render shows the fallback UI.
        return {error};
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {

        // Call optional callback
        if (this.props.onErrorCaptured) {
            this.props.onErrorCaptured(error, errorInfo);
        }
    }

    private resetErrorBoundary = () => {
        this.setState({error: null});
    };

    render() {
        const {error} = this.state;
        const {children, fallbackRender} = this.props;
        if (!error) return children;
        const fallbackProps: ErrorFallbackProps = {error, resetErrorBoundary: this.resetErrorBoundary};
        if (fallbackRender) return fallbackRender(fallbackProps);
        return <ErrorFallback {...fallbackProps} />;
    }
}


/// Helper component
function ErrorFallback(props: ErrorFallbackProps) {
    const {error, resetErrorBoundary} = props;


    // Hooks
    const t = useTranslations('common');


    // Render
    return (
        <div role="alert" className={styles.errorFallback}>
            <div className={styles.headline}>
                {t('errorBoundary.headline')}
            </div>
            <pre className={styles.errorMessage}>
                {error.message || t('errorBoundary.message')}
            </pre>
            <button
                type="button"
                className={classNames(styles.tryAgainButton, 'button')}
                onClick={resetErrorBoundary}
            >
                {t('errorBoundary.buttonRetry')}
            </button>
        </div>
    );
}
